import {
  Cable,
  Database,
  DatabaseBackup,
  KeyRound,
  RefreshCw,
  Trash2,
  ShieldCheck,
  Settings,
  RotateCcw,
  Workflow,
  Sprout,
  ShoppingBag,
  Users,
  ArrowRightLeft,
  Calculator,
  Coins,
  DownloadCloud,
} from 'lucide-react';
import type { ActionDefinition, CategoryMeta } from './types';

// ─── Categories ─────────────────────────────────

export const CATEGORIES: CategoryMeta[] = [
  { id: 'platform-fixes', label: 'Platform Fixes', icon: Cable },
  { id: 'data-operations', label: 'Data Operations', icon: Database },
  { id: 'queue-operations', label: 'Queue Operations', icon: Workflow },
  { id: 'system-maintenance', label: 'System Maintenance', icon: Settings },
];

// ─── Action Registry ────────────────────────────
// To add a new quick fix: append one object here. No component changes needed.

export const ACTIONS: ActionDefinition[] = [
  // ── Platform Fixes ────────────────────────────
  {
    id: 'fix-google-ads-mcc',
    name: 'Fix Google Ads MCC',
    description: 'Discover and set MCC loginCustomerId for all Google Ads connections.',
    longDescription:
      'Scans all active Google Ads connections, calls listAccessibleCustomers to find the MCC manager account, and stores the loginCustomerId in connection metadata. Fixes USER_PERMISSION_DENIED (403) errors for accounts under an MCC manager. Safe to run multiple times — already-fixed connections are skipped.',
    icon: Cable,
    category: 'platform-fixes',
    dangerLevel: 'safe',
    endpoint: '/api/v1/admin/fix-google-ads-mcc',
    fields: [],
  },
  {
    id: 'backfill-google-ads-legacy',
    name: 'Backfill Google Ads from Legacy',
    description: 'Restore managerAccountId + loginCustomerId on migrated Google Ads connections.',
    longDescription:
      'For every active Google Ads connection whose store has a legacyId, re-reads the legacy google_ads row and writes authoritative metadata: loginCustomerId (from manager_account_id, required by the Google Ads API as the login-customer-id header on MCC-owned accounts), managerAccountId/managerAccountName/hasManagerAccount (display), adAccountName, and refreshes externalAccountId. Also seeds developerToken from config when missing. For non-MCC accounts, clears any stale loginCustomerId. Tokens are not touched — use the Reimport Credentials route if tokens themselves need refreshing. Safe to run multiple times.',
    icon: DatabaseBackup,
    category: 'platform-fixes',
    dangerLevel: 'safe',
    endpoint: '/api/v1/admin/operations/backfill-google-ads',
    fields: [],
  },
  {
    id: 'refresh-tiktok-shop-ciphers',
    name: 'Refresh TikTok Shop Ciphers',
    description: 'Rediscover shop_cipher for every active TikTok Shop connection.',
    longDescription:
      'For every active TikTok Shop connection, calls getAuthorizedShops() and persists the returned shop_cipher + shopName to metadata, clearing the stale legacy sellerCipher. Fixes the silent-zero bug where Data Compass (/analytics/202405/shop/performance) returns 200 OK with zero-filled intervals when a legacy-migrated connection carries an old sellerCipher instead of the newer shopCipher — backfills appear to succeed but no real data lands. Safe to run multiple times — connections whose shopCipher already matches the live API are skipped.',
    icon: KeyRound,
    category: 'platform-fixes',
    dangerLevel: 'safe',
    endpoint: '/api/v1/admin/operations/refresh-tiktok-shop-ciphers',
    fields: [],
  },
  {
    id: 'repair-shopify-urls',
    name: 'Repair Shopify Store URLs',
    description: 'Fix missing or incorrect myshopify.com domains on migrated Shopify connections.',
    longDescription:
      "Scans all stores imported from the legacy platform and checks their Shopify connection for a valid myshopify.com domain in externalAccountId and metadata.shopDomain. If missing or set to a display name instead of the actual domain, it resolves the correct value from the legacy database's store.name field and patches the connection. Safe to run multiple times — already-correct connections are skipped.",
    icon: ShoppingBag,
    category: 'platform-fixes',
    dangerLevel: 'safe',
    endpoint: '/api/v1/migration/legacy/repair-shopify',
    fields: [],
  },
  {
    id: 'sync-agency-memberships',
    name: 'Sync Agency Store Memberships',
    description: 'Grant agency members access to all stores assigned to their agency.',
    longDescription:
      'Scans all agencies and their members, then creates StoreMembership rows (source: "agency") for every store-member pair that should have access based on grantAllStores or explicit store assignments. Already-correct memberships are skipped. Use this after bulk-assigning stores to agencies or if agency members report missing store access. Safe to run multiple times.',
    icon: Users,
    category: 'platform-fixes',
    dangerLevel: 'safe',
    endpoint: '/api/v1/admin/sync-agency-memberships',
    fields: [],
  },

  // ── Data Operations ───────────────────────────
  {
    id: 'seed-configs',
    name: 'Seed Ingestion Configs',
    description: 'Create missing ingestion configs for connected platforms.',
    longDescription:
      'Scans all stores and their active connections, then creates ingestion configs for any platform that is connected but has no config. Existing configs are not modified. Optionally scope to a single store.',
    icon: Sprout,
    category: 'data-operations',
    dangerLevel: 'safe',
    endpoint: '/api/v1/ingestion/admin/seed-configs',
    fields: [{ name: 'storeId', label: 'Store (optional)', type: 'store-select', required: false }],
    buildPayload: (values) => {
      const payload: Record<string, unknown> = {};
      if (values['storeId']) payload['storeId'] = Number(values['storeId']);
      return payload;
    },
  },
  {
    id: 'verify-data',
    name: 'Verify Data Coverage',
    description: "Check for data gaps in a store's ingested data.",
    longDescription:
      'Queries ClickHouse to verify that data exists for every expected day in the date range. Reports coverage percentage, missing days, and row counts. Does not modify any data.',
    icon: ShieldCheck,
    category: 'data-operations',
    dangerLevel: 'safe',
    endpoint: '/api/v1/ingestion/admin/verify',
    fields: [
      { name: 'storeId', label: 'Store', type: 'store-select', required: true },
      { name: 'platform', label: 'Platform', type: 'platform-select', required: true },
      { name: 'startDate', label: 'Start Date', type: 'date', required: true },
      { name: 'endDate', label: 'End Date', type: 'date', required: true },
    ],
    buildPayload: (values) => ({
      storeId: Number(values['storeId']),
      platform: values['platform'],
      startDate: values['startDate'],
      endDate: values['endDate'],
    }),
  },
  {
    id: 'clear-data',
    name: 'Clear Platform Data',
    description: 'Delete all ClickHouse data for a store + platform.',
    longDescription:
      'Permanently deletes all ingested data (analytics rows, ad events, orders) from ClickHouse for the specified store and platform. Run history is also cleared. This cannot be undone — you will need to re-backfill after clearing.',
    icon: Trash2,
    category: 'data-operations',
    dangerLevel: 'destructive',
    endpoint: '/api/v1/ingestion/admin/clear-data',
    fields: [
      { name: 'storeId', label: 'Store', type: 'store-select', required: true },
      { name: 'platform', label: 'Platform', type: 'platform-select', required: true },
    ],
    buildPayload: (values) => ({
      storeId: Number(values['storeId']),
      platform: values['platform'],
    }),
  },

  // ── Queue Operations ──────────────────────────
  {
    id: 'retry-all-failed',
    name: 'Retry All Failed Jobs',
    description: 'Re-enqueue all failed jobs in a queue for retry.',
    longDescription:
      'Moves all failed jobs back to the waiting state so workers can re-process them. Use this after fixing the root cause of failures (e.g., a connection issue or API change). Jobs that fail again will return to the failed state.',
    icon: RotateCcw,
    category: 'queue-operations',
    dangerLevel: 'warning',
    endpoint: '/api/v1/ingestion/admin/retry-all-failed',
    fields: [{ name: 'queueName', label: 'Queue', type: 'queue-select', required: true }],
    buildPayload: (values) => ({ queueName: values['queueName'] }),
  },
  {
    id: 'drain-queue',
    name: 'Drain Queue',
    description: 'Remove all jobs (waiting + failed) from a queue.',
    longDescription:
      'Permanently removes all waiting and failed jobs from the queue. Active jobs that are currently processing will finish, but no new jobs will be picked up. Use with caution — you may need to re-trigger backfills or pulls after draining.',
    icon: Trash2,
    category: 'queue-operations',
    dangerLevel: 'destructive',
    endpoint: '/api/v1/ingestion/admin/drain-queue',
    fields: [{ name: 'queueName', label: 'Queue', type: 'queue-select', required: true }],
    buildPayload: (values) => ({ queueName: values['queueName'] }),
  },

  // ── System Maintenance ────────────────────────
  {
    id: 'sync-schedules',
    name: 'Sync Daily Schedules',
    description: 'Re-synchronize all daily pull cron schedules from the database.',
    longDescription:
      "Reads all active ingestion configs and recreates their BullMQ repeatable job schedules. Use this if schedules seem out of sync (e.g., after a deployment or if daily pulls stopped firing). Safe to run anytime — it's idempotent.",
    icon: RefreshCw,
    category: 'system-maintenance',
    dangerLevel: 'safe',
    endpoint: '/api/v1/ingestion/admin/sync-schedules',
    fields: [],
  },
  {
    id: 'backfill-access-grants',
    name: 'Backfill Access Grants',
    description: 'Populate access_grants table from existing store_memberships.',
    longDescription:
      'Reads all store_memberships and creates corresponding access_grant rows for each (user, store, source) combination. Retries failed rows up to 3 times with exponential backoff. Idempotent — already-existing grants are skipped. Run this before switching the read source to "grants".',
    icon: ArrowRightLeft,
    category: 'system-maintenance',
    dangerLevel: 'safe',
    endpoint: '/api/v1/admin/operations/backfill-access-grants',
    fields: [],
  },

  // ── Data-accuracy remediations ────────────────
  {
    id: 'refresh-shopify-currency',
    name: 'Refresh Shopify Currencies',
    description: "Re-fetch each Shopify shop's currency and store it on the connection.",
    longDescription:
      "For every active Shopify connection, calls the Shopify Admin API (getShop) and persists the shop currency to connection.currency. The store's display currency is derived from this, so connections that predate currency capture (or whose shop changed currency) would otherwise default to USD and render non-USD amounts with a $ sign. Safe to run repeatedly — connections whose stored currency already matches the live shop are skipped.",
    icon: Coins,
    category: 'platform-fixes',
    dangerLevel: 'safe',
    endpoint: '/api/v1/admin/operations/refresh-shopify-currency',
    fields: [],
  },
  {
    id: 'recompute-shopify-customers',
    name: 'Fix New/Returning Customers',
    description: 'Recompute new-vs-returning classification for a Shopify store.',
    longDescription:
      "Rewrites raw_orders.is_new_customer / customer_type from each customer's full order history (first-ever order = new; pre-window history => returning), fixing the lifetime-snapshot bug where a customer's first order was retroactively re-labelled returning. Leave the date range blank to rewrite the store's entire history (one-time remediation); set a range for a narrower window. recomputeMetrics is implied when a window is given so the dashboard tiles refresh.",
    icon: Users,
    category: 'data-operations',
    dangerLevel: 'warning',
    endpoint: '/api/v1/ingestion/admin/shopify-parity/recompute-customers',
    fields: [
      { name: 'storeId', label: 'Store', type: 'store-select', required: true },
      { name: 'startDate', label: 'Start Date (optional)', type: 'date', required: false },
      { name: 'endDate', label: 'End Date (optional)', type: 'date', required: false },
    ],
    buildPayload: (values) => {
      const payload: Record<string, unknown> = { storeId: Number(values['storeId']) };
      if (values['startDate']) payload['startDate'] = values['startDate'];
      if (values['endDate']) payload['endDate'] = values['endDate'];
      return payload;
    },
  },
  {
    id: 'recompute-shopify-metrics',
    name: 'Recompute Shopify Metrics',
    description: 'Re-aggregate shopify_daily_metrics from raw data + re-run the parity check.',
    longDescription:
      'Recomputes the precomputed Shopify daily metrics (revenue, Total Sales, gross, AOV, customers) from raw_orders / order_line_items / raw_refunds for the window, then re-runs the parity check. Use after a fix that changes the computation (e.g. the per-order gross fallback) or to clear drift. Does not pull fresh data from Shopify — use Backfill for that.',
    icon: RefreshCw,
    category: 'data-operations',
    dangerLevel: 'safe',
    endpoint: '/api/v1/ingestion/admin/shopify-parity/reconcile',
    fields: [
      { name: 'storeId', label: 'Store', type: 'store-select', required: true },
      { name: 'date', label: 'End Date (optional)', type: 'date', required: false },
      {
        name: 'days',
        label: 'Days Back (optional)',
        type: 'text',
        required: false,
        placeholder: '1',
      },
    ],
    buildPayload: (values) => {
      const payload: Record<string, unknown> = { storeId: Number(values['storeId']) };
      if (values['date']) payload['date'] = values['date'];
      if (values['days']) payload['days'] = Number(values['days']);
      return payload;
    },
  },
  {
    id: 'backfill-gmv-max',
    name: 'Backfill GMV Max Spend',
    description: 'Re-pull TikTok GMV Max ad spend for a date range (overwrites frozen-low days).',
    longDescription:
      'Re-pulls TikTok Shop GMV Max spend (raw_ad_events) for the date range and overwrites existing rows. Use to correct days whose spend was frozen low before TikTok finalized billed cost, or to fill missing recent days. Leave Store blank to run for all stores.',
    icon: DatabaseBackup,
    category: 'data-operations',
    dangerLevel: 'warning',
    endpoint: '/api/v1/ingestion/admin/gmv-max-backfill',
    fields: [
      {
        name: 'storeId',
        label: 'Store (optional, blank = all)',
        type: 'store-select',
        required: false,
      },
      { name: 'startDate', label: 'Start Date', type: 'date', required: true },
      { name: 'endDate', label: 'End Date', type: 'date', required: true },
    ],
    buildPayload: (values) => {
      const payload: Record<string, unknown> = {
        startDate: values['startDate'],
        endDate: values['endDate'],
      };
      if (values['storeId']) payload['storeId'] = Number(values['storeId']);
      return payload;
    },
  },
  {
    id: 'pull-now',
    name: 'Pull Now',
    description:
      'Trigger an immediate pull for a store + platform (re-pulls the late-arrival window).',
    longDescription:
      "Runs an immediate daily-style pull for the store and platform, re-pulling that platform's late-arrival window and overwriting recent days. For TikTok Shop this covers the last 14 days, correcting days that were frozen at their early, partially-settled value. Use to refresh a store right away or to pick up late-settling data without waiting for the scheduled pull. For deeper historical correction beyond the late-arrival window, use a dated backfill.",
    icon: DownloadCloud,
    category: 'data-operations',
    dangerLevel: 'warning',
    endpoint: '/api/v1/ingestion/admin/pull-now',
    fields: [
      { name: 'storeId', label: 'Store', type: 'store-select', required: true },
      { name: 'platform', label: 'Platform', type: 'platform-select', required: true },
    ],
    buildPayload: (values) => ({
      storeId: Number(values['storeId']),
      platform: values['platform'],
    }),
  },
  {
    id: 'run-computation',
    name: 'Run Computation',
    description:
      'Trigger a precompute / aggregation job (baseline, attribution, rollup, correlation).',
    longDescription:
      'Runs one of the analytics computations: daily-baseline, attribution (HaloMMM), metrics-rollup, or correlation. Optionally scope to a store and a since/until window. Use to refresh precomputed tables after data changes.',
    icon: Calculator,
    category: 'system-maintenance',
    dangerLevel: 'safe',
    endpoint: '/api/v1/ingestion/admin/compute',
    fields: [
      {
        name: 'computation',
        label: 'Computation',
        type: 'select',
        required: true,
        options: [
          { value: 'baseline', label: 'Baseline' },
          { value: 'attribution', label: 'Attribution' },
          { value: 'rollup', label: 'Metrics Rollup' },
          { value: 'correlation', label: 'Correlation' },
          { value: 'multiSignal', label: 'Multi-Signal' },
        ],
      },
      { name: 'storeId', label: 'Store', type: 'store-select', required: true },
      { name: 'startDate', label: 'Start Date', type: 'date', required: true },
      { name: 'endDate', label: 'End Date', type: 'date', required: true },
    ],
    buildPayload: (values) => ({
      storeId: Number(values['storeId']),
      startDate: values['startDate'],
      endDate: values['endDate'],
      computations: [values['computation']],
    }),
  },
];
